import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { callApiAction } from "../../store/actions/commonAction";
import {
  FetchProductionPlanningApi,
  FetchProductionPurchaseApi,
} from "../../apis/ProductionPlanning.api";
import ProductionPurchaseListUi from "./ProductionPurchaseListUi";
import { findObjectKeyByValue, titleCase } from "../../utils/main";
import {
  MATERIAL_TYPE,
  PRODUCTION_PURCHASE_STATUS,
  REQUEST_TYPE,
  SNACK_BAR_VARIETNS,
} from "../../utils/constants";
import moment from "moment";
import { Chip, Grid, IconButton, ListItem, Typography } from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import { closeModal, openModal } from "../../store/actions/modalAction";
import MessageDilog from "../../components/MessageDilog";
import { callSnackBar } from "../../store/actions/snackbarAction";
import { UpdateProductionPurchaseStatusApi } from "../../apis/purchaseRequest.api";
import AsyncDropDown from "../../components/inputs/AsyncDropDown";
import { FetchvendorRawMaterialWiseApi } from "../../apis/vendor.api";
import CustomDialog from "../../components/layouts/common/CustomDialog";
import CustomInput from "../../components/inputs/CustomInputs";

const VendorSelectionModal = ({ params, nextStatus, onConfirm }) => {
  const dispatch = useDispatch();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleConfirm = (e) => {
    e.preventDefault();
    if (vendor) {
      onConfirm(vendor);
    }
  };

  return (
    <CustomDialog
      id="vendor-selection"
      loading={loading}
      title="Select Vendor for Order Placement"
      closeText="Cancel"
      confirmText="Place Order"
      onSubmit={handleConfirm}
    >
      <div style={{ marginTop: 20 }}>
        <AsyncDropDown
          Value={vendor}
          lazyFun={async (para) =>
            await FetchvendorRawMaterialWiseApi({
              ...para,
              allStatus: true,
              materialId: params.materialDetails?._id,
            })
          }
          OptionComponent={({ option, ...rest }) => (
            <ListItem {...rest}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <span>{option.name}</span>
                <span style={{ color: "#666", fontSize: "0.875rem" }}>
                  Lead Time: {option.matchedMaterial?.lead_time || 0}{" "}
                  {Number(option.matchedMaterial?.lead_time) === 1
                    ? "Day"
                    : "Days"}
                </span>
              </div>
            </ListItem>
          )}
          onChange={(changedVal) => {
            setVendor(changedVal);
          }}
          titleKey={"name"}
          valueKey={"_id"}
          InputComponent={(params) => (
            <CustomInput
              {...params}
              label="Select Vendor *"
              placeholder="Search and select vendor"
              margin="dense"
              InputLabelProps={{ shrink: true }}
            />
          )}
        />

        {/* Show selected vendor's lead time below the dropdown */}
        {vendor && (
          <div
            style={{
              marginTop: 10,
              padding: 8,
              backgroundColor: "#f5f5f5",
              borderRadius: 4,
            }}
          >
            <Typography variant="body2" color="textSecondary">
              Selected Vendor Lead Time:{" "}
              {vendor.matchedMaterial?.lead_time || 0}{" "}
              {Number(vendor.matchedMaterial?.lead_time) === 1 ? "Day" : "Days"}
            </Typography>
          </div>
        )}
      </div>
    </CustomDialog>
  );
};
const ActionComponent = ({ params, onApprove }) => {
  const { status, requestType } = params;

  let showApproveButton = false;
  let nextStatusForApproval = null;
  let actionTextForApproval = "";
  let requiresVendorSelection = false;

  switch (status) {
    case PRODUCTION_PURCHASE_STATUS.PENDING:
      showApproveButton = true;
      nextStatusForApproval = PRODUCTION_PURCHASE_STATUS.ORDER_PLACED;
      actionTextForApproval = "place order";
      requiresVendorSelection = true; // ✅ Needs vendor for order placement
      break;
    case PRODUCTION_PURCHASE_STATUS.ORDER_PLACED:
      showApproveButton = true;
      nextStatusForApproval = PRODUCTION_PURCHASE_STATUS.ORDER_RECEIVED;
      actionTextForApproval = "mark as received";
      requiresVendorSelection = false;
      break;
    case PRODUCTION_PURCHASE_STATUS.ORDER_RECEIVED:
      showApproveButton = false;
      break;
    default:
      showApproveButton = false;
  }

  if (showApproveButton && nextStatusForApproval && actionTextForApproval) {
    return (
      <IconButton
        color="success"
        onClick={() => {
          if (requiresVendorSelection) {
            // Open vendor selection modal for PENDING → ORDER_PLACED
            onApprove(
              params,
              nextStatusForApproval,
              actionTextForApproval,
              true,
            );
          } else {
            // Simple confirmation for ORDER_PLACED → ORDER_RECEIVED
            onApprove(
              params,
              nextStatusForApproval,
              actionTextForApproval,
              false,
            );
          }
        }}
      >
        <CheckCircle />
      </IconButton>
    );
  }

  return null;
};

const ProductionPurchaseListController = () => {
  const dispatch = useDispatch();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    pageNo: 1,
    pageSize: 10,
    search: "",
    searchable: ["name"],
    sort: "createdAt",
    sortDirection: -1,
  });

  const handleApprove = (
    params,
    nextStatus,
    actionText,
    requiresVendorSelection = false,
  ) => {
    if (requiresVendorSelection) {
      // Show vendor selection modal for order placement
      dispatch(
        openModal(
          <VendorSelectionModal
            params={params}
            nextStatus={nextStatus}
            onConfirm={(selectedVendor) =>
              confirmApprove(params, nextStatus, selectedVendor)
            }
          />,
          "sm",
          false,
          "vendor-selection",
        ),
      );
    } else {
      // Simple confirmation dialog for other actions
      dispatch(
        openModal(
          <MessageDilog
            onSubmit={(e) => {
              if (e && e.preventDefault) e.preventDefault();
              confirmApprove(params, nextStatus);
            }}
            title="Confirm Action"
            modalId="messagedialog"
            message={`Are you sure you want to ${actionText} for "${params?.materialDetails?.name}"?`}
            confirmText={titleCase(actionText)}
          />,
          "sm",
          false,
          "messagedialog",
        ),
      );
    }
  };
  const confirmApprove = (params, nextStatus, selectedVendor = null) => {
    dispatch(closeModal("vendor-selection")); // Close vendor modal if open
    dispatch(closeModal("messagedialog")); // Close confirmation modal if open

    // Prepare payload
    const payload = {
      id: params._id,
      status: nextStatus,
    };

    // Add vendor if provided (for ORDER_PLACED status change)
    if (selectedVendor) {
      payload.vendorId = selectedVendor._id;
      payload.vendorName = selectedVendor.name;
    }

    dispatch(
      callApiAction(
        async () => await UpdateProductionPurchaseStatusApi(payload),
        async () => {
          const successMsg =
            nextStatus === PRODUCTION_PURCHASE_STATUS.ORDER_PLACED
              ? `Order placed successfully with ${selectedVendor?.name || "vendor"}`
              : "Order marked as received successfully";
          dispatch(callSnackBar(successMsg, SNACK_BAR_VARIETNS.suceess));
          fetchList();
        },
        (err) => {
          dispatch(
            callSnackBar(
              err || "Failed to update status",
              SNACK_BAR_VARIETNS.error,
            ),
          );
        },
      ),
    );
  };
  const getStatusLabelAndColor = (status) => {
    switch (status) {
      case PRODUCTION_PURCHASE_STATUS.PENDING:
        return { label: "Pending", color: "default" };
      case PRODUCTION_PURCHASE_STATUS.ORDER_PLACED:
        return { label: "Order Placed", color: "warning" };

      case PRODUCTION_PURCHASE_STATUS.ORDER_RECEIVED:
        return { label: "Order Received", color: "secondary" };
      default:
        return { label: "NA", color: "default" };
    }
  };
  const columns = useMemo(
    () => [
      {
        id: 1,
        fieldName: "",
        label: "Date",
        align: "left",
        sort: true,
        renderValue: (params, setParams) =>
          moment(params?.date).format("DD/MM/YYYY") || "NA",
      },

      {
        id: 2,
        fieldName: "",
        label: "Material Type",
        align: "left",
        sort: true,
        renderValue: (params, setParams) =>
          findObjectKeyByValue(params?.materialType, MATERIAL_TYPE) || "NA",
      },
      {
        id: 3,
        fieldName: "",
        label: "Material Name",
        align: "left",
        sort: true,
        renderValue: (params, setParams) =>
          params?.materialDetails?.name || "NA",
      },
      {
        id: 3,
        fieldName: "",
        label: "Request ID",
        align: "left",
        sort: true,
        renderValue: (params, setParams) => params?.requestId,
      },
      {
        id: 5,
        fieldName: "",
        label: "Quantity",
        align: "left",
        sort: true,
        renderValue: (params, setParams) => params?.quantity || "NA",
      },
      {
        id: 4,
        fieldName: "",
        label: "Status",
        align: "left",
        sort: true,
        renderValue: (params, setParams) => {
          const { label, color } = getStatusLabelAndColor(params?.status);
          return <Chip label={label} color={color} size="small" />;
        },
      },
      {
        id: 7,
        fieldName: "",
        label: "Action",
        align: "left",
        sort: false,
        renderValue: (params, setParams) => {
          return <ActionComponent params={params} onApprove={handleApprove} />;
        },
      },
    ],
    [dispatch, filters],
  );

  const fetchList = () => {
    setLoading(true);
    dispatch(
      callApiAction(
        async () => await FetchProductionPurchaseApi(filters),
        (response) => {
          setList(response);
          setLoading(false);
        },
        (err) => {
          setLoading(false);
        },
      ),
    );
  };

  useEffect(() => {
    fetchList();
  }, [filters]);

  return (
    <ProductionPurchaseListUi
      list={list}
      loading={loading}
      filters={filters}
      setFilters={setFilters}
      columns={columns}
    />
  );
};

export default ProductionPurchaseListController;
